import { Map } from "immutable";
import { isEmpty } from "lodash";

import { immutableRecords } from "../../../types";
import { keyArrayFromMap } from "../../../utils/immutableExtensions";
import { dependencySelectors } from "../../dependencies";
import { itemSelectors } from "../../items";
import { isDependencyViolated } from "./selectors";

const reducer = (state = Map(), action, newGlobalState, prevGlobalState) => {
  // If we have no data loaded yet, don't bother deriving anything
  if (!newGlobalState.isDataLoaded) {
    return state;
  }

  // Grab some stuff we will need
  const newItemsById = itemSelectors.getItemsById(newGlobalState);
  const newDependenciesById = dependencySelectors.getDependenciesById(newGlobalState);
  const prevDependenciesById = dependencySelectors.getDependenciesById(prevGlobalState);

  const newItemDates = newGlobalState.display.itemDates;
  const prevItemDates = prevGlobalState.display.itemDates;

  const isDataChanged = newItemDates !== prevItemDates || newDependenciesById !== prevDependenciesById;

  if (!isDataChanged) {
    return state;
  }

  let newState = state;

  // Which dependencies will we need to re-check to see if they are violated?
  //   - Any related to items that have changed dates
  //   - Plus any new dependencies
  //   - Also need to delete old ones

  // Get the dates and find those that changed

  const changedItemDates = newItemDates.filter((n, id) => n !== prevItemDates.get(id));
  const itemChangeDependencyIds = changedItemDates.reduce((result, d, id) => {
    const newItem = newItemsById.get(id);
    result.push(...newItem.blockingDependencies.toArray());
    result.push(...newItem.blockedByDependencies.toArray());
    return result;
  }, []);

  // Find any dateConstraints that have changed
  const newAndChangedIdSet = keyArrayFromMap(newDependenciesById.filter((n, id) => n !== prevDependenciesById.get(id)));
  const brandNewDependencies = newDependenciesById.filter((n, id) => !prevDependenciesById.has(id));
  const deletedIds = keyArrayFromMap(prevDependenciesById.filter((n, id) => !newDependenciesById.get(id)));

  // Delete any old ones we don't need
  if (deletedIds.length) {
    newState = newState.deleteAll(deletedIds);
  }

  // And bring across any new ones ready for the proper updates below
  if (brandNewDependencies.size) {
    newState = newState.merge(brandNewDependencies.map(d => immutableRecords.ValidatedDependencyRecord(d)));
  }

  const affectedIds = [...itemChangeDependencyIds, ...newAndChangedIdSet];

  newState = newState.withMutations(s => {
    affectedIds.forEach(cId => {
      const dependency = newDependenciesById.get(cId);
      const isViolated = isDependencyViolated(dependency, newItemDates);
      const dependencyType = dependency.dependencyType;
      const rec = newState.get(cId);
      let changes = {};
      if (isViolated !== rec.isViolated) {
        changes.isViolated = isViolated;
      }
      if (dependencyType !== rec.dependencyType) {
        changes.dependencyType = dependencyType;
      }
      if (!isEmpty(changes)) {
        s.update(cId, d => d.merge(changes));
      }
    });
  });

  return newState;
};

export default reducer;
